/* import __COLOCATED_TEMPLATE__ from './teammate-removal-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'embercom/services/intl';
import type Admin from 'embercom/models/admin';
import type NotificationsService from 'embercom/services/notifications-service';
export interface Args {
  onClose: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class TeammateRemovalModal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @tracked selectedAdminsForRemoval: string[] = [];

  syncCheckboxState = modifier(
    (element: HTMLInputElement, [checked, indeterminate]: [boolean, boolean]) => {
      element.checked = checked;
      element.indeterminate = indeterminate;
    },
  );

  syncAdminCheckbox = modifier(
    (element: HTMLInputElement, [selectedIds, adminId]: [string[], string]) => {
      element.checked = selectedIds.includes(adminId);
    },
  );

  get currentAdmin() {
    return this.appService.app.currentAdmin;
  }

  get adminsForDisplay() {
    let currentId = this.currentAdmin.id;
    return [...this.appService.app.humanAdmins].sort((a: Admin, b: Admin) => {
      if (a.id === currentId) {
        return -1;
      }
      if (b.id === currentId) {
        return 1;
      }
      return 0;
    });
  }

  get selectableAdmins() {
    return this.adminsForDisplay.filter((admin: Admin) => admin.id !== this.currentAdmin.id);
  }

  get allSelected() {
    return this.selectedAdminsForRemoval.length === this.selectableAdmins.length;
  }

  get isIndeterminate() {
    return (
      this.selectedAdminsForRemoval.length > 0 &&
      this.selectedAdminsForRemoval.length < this.selectableAdmins.length
    );
  }

  @action toggleAdminForRemoval(admin: Admin) {
    if (this.selectedAdminsForRemoval.includes(admin.id)) {
      this.selectedAdminsForRemoval = this.selectedAdminsForRemoval.filter((id) => id !== admin.id);
    } else {
      this.selectedAdminsForRemoval = [...this.selectedAdminsForRemoval, admin.id];
    }
  }

  @action toggleAllTeammatesForRemoval() {
    if (this.selectedAdminsForRemoval.length < this.selectableAdmins.length) {
      this.selectedAdminsForRemoval = this.selectableAdmins.map((admin: Admin) => admin.id);
    } else {
      this.selectedAdminsForRemoval = [];
    }
  }

  @action async removeTeammates() {
    // Capture state before closing — the component will be destroyed when onClose triggers re-render
    let confirmService = this.intercomConfirmService;
    let intl = this.intl;

    // Show confirmation via the global service (independent of component lifecycle)
    let confirmed = await confirmService.confirm({
      title: intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.confirmation-modal.title',
      ),
      body: intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.confirmation-modal.body',
        {
          numberOfAdmins: this.selectedAdminsForRemoval.length,
        },
      ),
      confirmButtonText: intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.confirmation-modal.confirm',
      ),
      primaryButtonType: 'primary-destructive',
      cancelButtonText: intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.confirmation-modal.cancel',
      ),
    });

    if (confirmed) {
      // Close the main modal first
      this.args.onClose();
    }
  }

  async removeSelectedTeammates() {
    // Show an alert right now without the actual logic
    let firstSelectedId = this.selectedAdminsForRemoval[0];
    let firstSelectedAdmin = firstSelectedId
      ? this.adminsForDisplay.find((a: Admin) => a.id === firstSelectedId)
      : undefined;
    this.notificationsService.notifyConfirmation(
      this.intl.t(
        'signup.teams.pricing5.annual-plans.configure-plan.teammate-removal-modal.removal-success',
        {
          removedAdmins: this.selectedAdminsForRemoval.length,
          removedAdminName: firstSelectedAdmin?.name,
        },
      ),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::TeammateRemovalModal': typeof TeammateRemovalModal;
  }
}
